import React, { useEffect, useState, useMemo, useRef } from "react";
import { getDirectusClient } from "../../lib/directus";
import {
  Table,
  Button,
  Spin,
  Row,
  Col,
  List,
  DatePicker,
  Space,
  Tag,
  Alert,
  Select,
} from "antd";
import { readUsers, readItems, readMe } from "@directus/sdk";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import moment from "moment";
import validarpermiso from "../../lib/utils";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import InfiniteScroll from "react-infinite-scroll-component";
import { Divider, Skeleton } from "antd";

const customPopupStyle = {
  vendorPopup: {
    backgroundColor: "#2196f3",
    color: "white",
    padding: "10px 15px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  clientPopup: {
    backgroundColor: "#f44336",
    color: "white",
    padding: "10px 15px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  title: {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  info: {
    fontSize: "14px",
  },
  status: {
    padding: "4px 8px",
    borderRadius: "3px",
    backgroundColor: "rgba(255,255,255,0.2)",
    marginTop: "8px",
    display: "inline-block",
  },
};

// Keep the createCustomIcon and vendorIcon for vendors
const createCustomIcon = (svg, color) => {
  return L.divIcon({
    className: "custom-icon",
    html: svg,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

const vendorIcon = createCustomIcon(
  `
  <svg width="40px" height="40px" viewBox="-1 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <g id="user" transform="translate(-3 -2)">
      <path id="secondary" fill="#2196f3" d="M9,15h6a5,5,0,0,1,5,5h0a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1H4a5,5,0,0,1,5-5Z"/>
      <path id="primary" d="M20,20h0a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1H4a5,5,0,0,1,5-5h6A5,5,0,0,1,20,20ZM12,3a4,4,0,1,0,4,4A4,4,0,0,0,12,3Z" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
    </g>
  </svg>
  `,
  "#2196f3"
);

// Create default colored markers for clients
const clientIcons = {
  activo: L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  "gestion iniciada": L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  gestionado: L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  "cliente ausente": L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
};

const weekdayMap = {
  monday: { id: 1, name: "Lunes" },
  tuesday: { id: 2, name: "Martes" },
  wednesday: { id: 3, name: "Miércoles" },
  thursday: { id: 4, name: "Jueves" },
  friday: { id: 5, name: "Viernes" },
  saturday: { id: 6, name: "Sábado" },
  sunday: { id: 7, name: "Domingo" },
};

const getMapCenter = (selectedVendedor, gestionRutero) => {
  // Si hay vendedor seleccionado, usar sus coordenadas
  if (selectedVendedor?.ultima_coordenada) {
    const [lat, lng] = selectedVendedor.ultima_coordenada
      .split(",")
      .map((coord) => parseFloat(coord.trim()));

    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
  }

  // Si hay gestiones, usar el centro de todas las ubicaciones
  if (gestionRutero.length > 0) {
    const lats = gestionRutero.map((g) => parseFloat(g["570a31e0"]?.latitude));
    const lngs = gestionRutero.map((g) => parseFloat(g["570a31e0"]?.longitude));

    const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

    if (!isNaN(centerLat) && !isNaN(centerLng)) {
      return [centerLat, centerLng];
    }
  }

  // Coordenadas por defecto (Bogotá)
  return [8.9760285, -79.5181536];
};

const Reportemaps = () => {
  const [vendedores, setVendedores] = useState([]);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [loadingVendedores, setLoadingVendedores] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const [gestionRutero, setGestionRutero] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment());
  const userRoleID = useSelector((state) => state.userdata.role?.name);
  const [showNoCoordsWarning, setShowNoCoordsWarning] = useState(false);
  const [mapCenter, setMapCenter] = useState([8.9760285, -79.5181536]);
  const mapRef = useRef(null);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const validateSession = async () => {
    try {
      const directus = await getDirectusClient();
      await directus.request(readMe());
      return true;
    } catch (error) {
      window.location.reload();
      return false;
    }
  };

  const getVendedores = async () => {
    setLoadingVendedores(true);

    if (await validateSession()) {
      try {
        const directus = await getDirectusClient();
        const response = await directus.request(
          readUsers({
            fields: ["*", "role.*"],
            filter: {
              _and: [{ role: { name: { _eq: "vendedor rutas" } } }],
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                  }
                : {}),
            },
            limit: -1,
          })
        );

        setVendedores(response);
      } catch (error) {
        console.error("Error fetching vendedores:", error);
      } finally {
        setLoadingVendedores(false);
      }
    }
  };

  const getClientesRuta = async (vendedorId) => {
    setLoadingClientes(true);
    if (await validateSession()) {
      try {
        const directus = await getDirectusClient();
        const dayName = selectedDate.format("dddd").toLowerCase();
        const dayId = weekdayMap[dayName].id;

        // Get vendor data
        const vendorData = await directus.request(
          readUsers({
            fields: ["id", "first_name", "last_name", "ultima_coordenada"],
            filter: { id: { _eq: vendedorId } },
          })
        );

        // Get all clients from the vendor's circuit for the selected day
        const circuitosData = await directus.request(
          readItems("circuitos", {
            fields: [
              "id",
              "clientes.clientes_id.id",
              "clientes.clientes_id.first_name",
              "clientes.clientes_id.last_name",
              "clientes.clientes_id.latitude",
              "clientes.clientes_id.longitude",
            ],
            filter: {
              _and: [
                { vendedor: { _eq: vendedorId } },
                {
                  dias: {
                    dias_id: {
                      id: { _eq: dayId },
                    },
                  },
                },
              ],
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                  }
                : {}),
            },
          })
        );

        // Get gestiones for status information
        const gestionData = await directus.request(
          readItems("gestion_cliente_rutero", {
            fields: [
              "id",
              "date_created",
              "4d1bc69a",
              "facturacion",
              "id",
              "vendedor.first_name",
              "406b9912.estado",
              "334b85fa.id",
              "570a31e0.latitude",
              "570a31e0.longitude",
              "1a4578d2.first_name",
              "date_updated",
            ],
            alias: {
              "4d1bc69a": "estado",
              "406b9912": "estado",
              "334b85fa": "cliente",
              "570a31e0": "cliente",
              "1a4578d2": "cliente",
            },
            filter: {
              _and: [
                {
                  vendedor: { _eq: vendedorId },
                },
                {
                  date_created: {
                    _between: [
                      selectedDate.format("YYYY-MM-DD") + "T00:00:00-05:00",
                      selectedDate.format("YYYY-MM-DD") + "T23:59:59-05:00",
                    ],
                  },
                },
              ],
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                  }
                : {}),
            },
            sort: ["id"],
            limit: -1,
          })
        );

        // Create a map of client statuses and dates from gestiones
        const clientStatusMap = new Map();
        const clientDateMap = new Map();
        gestionData.forEach((gestion) => {
          if (gestion["334b85fa"]?.id) {
            // Get the status from the correct field
            const status = gestion["406b9912"]?.estado || gestion["4d1bc69a"];
            clientStatusMap.set(gestion["334b85fa"].id, status);
            // Store the creation date
            clientDateMap.set(gestion["334b85fa"].id, gestion.date_updated);
          }
        });

                 // Combine circuit clients with their status from gestiones
         const allClients = circuitosData.flatMap(
           (circuito) =>
             circuito.clientes
               ?.map((cliente) => {
                 let latitude = cliente.clientes_id.latitude;
                 let longitude = cliente.clientes_id.longitude;

                 // Get the status from the map, default to "activo" if not found
                 const status =
                   clientStatusMap.get(cliente.clientes_id.id) || "activo";
                 // Get the creation date from the map
                 const date_updated = clientDateMap.get(cliente.clientes_id.id);

                 return {
                   id: cliente.clientes_id.id,
                   first_name: cliente.clientes_id.first_name,
                   last_name: cliente.clientes_id.last_name,
                   latitude: latitude,
                   longitude: longitude,
                   estado: status,
                   date_updated: date_updated,
                 };
               })
               .filter(
                 (cliente) =>
                   cliente.latitude &&
                   cliente.longitude &&
                   !isNaN(cliente.latitude) &&
                   !isNaN(cliente.longitude) &&
                   parseFloat(cliente.latitude) !== 0 &&
                   parseFloat(cliente.longitude) !== 0
               ) || []
         );

        setSelectedVendedor(vendorData[0]);
        setGestionRutero(allClients);
        setMapKey((prevKey) => prevKey + 1);
      } catch (error) {
        console.error("Error fetching gestion rutero:", error);
      } finally {
        setLoadingClientes(false);
      }
    }
  };

  const handleVendedorChange = (vendedorId) => {
    if (vendedorId) {
      const vendedor = vendedores.find((v) => v.id === vendedorId);
      setSelectedVendedor(vendedor);
      getClientesRuta(vendedorId);
    } else {
      setSelectedVendedor(null);
      setGestionRutero([]);
    }
  };

  const handleClienteChange = (clienteId) => {
    if (clienteId) {
      const cliente = gestionRutero.find((c) => c.id === clienteId);
      setSelectedCliente(cliente);
      handleClientClick(cliente);
    } else {
      setSelectedCliente(null);
    }
  };

  useEffect(() => {
    getVendedores();
  }, []);

  useEffect(() => {
    if (gestionRutero.length > 0) {
      const allInvalid = gestionRutero.every((cliente) => {
        const lat = parseFloat(cliente.latitude);
        const lng = parseFloat(cliente.longitude);
        return isNaN(lat) || isNaN(lng);
      });
      setShowNoCoordsWarning(allInvalid);
    } else {
      setShowNoCoordsWarning(false);
    }
  }, [gestionRutero]);

  const getClientIcon = (estado) => {
    const status = estado?.toLowerCase();
    return clientIcons[status] || clientIcons["cliente ausente"];
  };

  const handleClientClick = (cliente) => {
    const lat = parseFloat(cliente.latitude);
    const lng = parseFloat(cliente.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter([lat, lng]);
      if (mapRef.current) {
        mapRef.current.setView([lat, lng], 15);
      }
    }
  };

  return (
    <Layouts>
      {validarpermiso("rutas", "read", permisos) ? (
        <div style={{ padding: "24px" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {/* Controles superiores */}
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8}>
                <Space direction="vertical" style={{ width: "100%" }}>
                
                  <Space direction="horizontal" size="large">
                  <label style={{ fontWeight: "bold" }}>Fecha: </label>
                    <Tag color="blue">
                      {
                        weekdayMap[selectedDate.format("dddd").toLowerCase()]
                          .name
                      }
                    </Tag>
                  </Space>
                  <DatePicker
                    onChange={(date) => {
                      setSelectedDate(date || moment());
                      if (selectedVendedor) {
                        getClientesRuta(selectedVendedor.id);
                      }
                    }}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Space>
              </Col>
              <Col xs={24} sm={8}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <label style={{ fontWeight: "bold" }}>Vendedor:</label>
                  <Select
                    placeholder="Seleccionar vendedor"
                    loading={loadingVendedores}
                    onChange={handleVendedorChange}
                    style={{ width: "100%" }}
                    allowClear
                  >
                    {vendedores.map((vendedor) => (
                      <Select.Option key={vendedor.id} value={vendedor.id}>
                        {vendedor.first_name} {vendedor.last_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col xs={24} sm={8}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <label style={{ fontWeight: "bold" }}>Cliente:</label>
                  <Select
                    placeholder="Seleccionar cliente"
                    onChange={handleClienteChange}
                    style={{ width: "100%" }}
                    allowClear
                    disabled={!selectedVendedor || gestionRutero.length === 0}
                  >
                    {gestionRutero.map((cliente) => (
                      <Select.Option key={cliente.id} value={cliente.id}>
                        {cliente.first_name} {cliente.last_name} -{" "}
                        {cliente.estado}
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>

            {/* Mapa */}
            <div>
              <h3>Mapa de Gestión de Clientes</h3>
              <Spin spinning={loadingClientes}>
                {showNoCoordsWarning && (
                  <Alert
                    type="warning"
                    message="Advertencia"
                    description="Todos los clientes en este circuito no tienen coordenadas geográficas válidas registradas"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <MapContainer
                  key={mapKey}
                  center={mapCenter}
                  zoom={13}
                  style={{
                    height: "calc(100vh - 200px)",
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #f0f0f0",
                  }}
                  dragging={true}
                  touchZoom={false}
                  scrollWheelZoom={true}
                  doubleClickZoom={false}
                  boxZoom={true}
                  keyboard={false}
                  zoomControl={true}
                  ref={mapRef}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {selectedVendedor?.ultima_coordenada && (
                    <Marker
                      position={selectedVendedor.ultima_coordenada
                        .split(",")
                        .map((coord) => parseFloat(coord))}
                      icon={vendorIcon}
                    >
                      <Popup className="vendor-popup">
                        <div style={customPopupStyle.vendorPopup}>
                          <div style={customPopupStyle.title}>🚶‍♂️ Vendedor</div>
                          <div style={customPopupStyle.info}>
                            <div>
                              {selectedVendedor.first_name}{" "}
                              {selectedVendedor.last_name}
                            </div>
                            <small>
                              Última conexion:{" "}
                              {moment(selectedVendedor.date_updated).format(
                                "DD/MM/YYYY HH:mm"
                              )}
                              <br />
                              Ubicacion:{" "}
                              {selectedVendedor.ultima_coordenada
                                .split(",")
                                .map((coord) => parseFloat(coord))
                                .join(", ")}
                            </small>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  {gestionRutero.map((cliente) => {
                    if (!cliente?.latitude || !cliente?.longitude) return null;
                    const lat = parseFloat(cliente.latitude);
                    const lng = parseFloat(cliente.longitude);

                    if (isNaN(lat) || isNaN(lng)) return null;
                    console.log("cliente", cliente);

                    return (
                      <Marker
                        key={cliente.id}
                        position={[lat, lng]}
                        icon={getClientIcon(cliente.estado)}
                      >
                        <Popup>
                          <div
                            style={{
                              ...customPopupStyle.clientPopup,
                              backgroundColor:
                                cliente.estado === "activo"
                                  ? "rgb(54, 168, 244)"
                                  : cliente.estado === "gestion iniciada"
                                  ? "#FFC107"
                                  : cliente.estado === "gestionado"
                                  ? "rgb(20, 163, 4)"
                                  : "#f44336",
                            }}
                          >
                            <div style={customPopupStyle.title}>🧍‍♂️ Cliente</div>
                            <div style={customPopupStyle.info}>
                              <div>
                                {cliente.first_name} {cliente.last_name}
                              </div>
                              <div style={customPopupStyle.status}>
                                Estado: {cliente.estado}
                              </div>
                              {cliente.date_updated && (
                                <div style={customPopupStyle.status}>
                                  Fecha:{" "}
                                  {moment(cliente.date_updated).format(
                                    "DD/MM/YYYY HH:mm"
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}

                  {selectedVendedor?.ultima_coordenada &&
                    gestionRutero.length > 0 && (
                      <Polyline
                        positions={[
                          selectedVendedor.ultima_coordenada
                            .split(",")
                            .map((coord) => parseFloat(coord)),
                          ...gestionRutero
                            .filter(
                              (cliente) => cliente.latitude && cliente.longitude
                            )
                            .map((cliente) => [
                              parseFloat(cliente.latitude),
                              parseFloat(cliente.longitude),
                            ]),
                        ]}
                        color="blue"
                      />
                    )}
                </MapContainer>
              </Spin>
            </div>
          </Space>
        </div>
      ) : (
        <h3>No tiene permisos para ver rutas</h3>
      )}
    </Layouts>
  );
};
export default Reportemaps;
